import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { num, pct, usd0 } from './ForecastWidgets';

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

// The ladder and reallocation both price markets in whichever unit the data can
// actually support: cost of marketing where the sales sample carries it, cost
// per appointment (~35x the sample) where it doesn't.
const costFmt = (unit) => (unit === 'cost_of_marketing' ? pct : usd0);

const ZONE_META = {
  proven: {
    color: 'success',
    label: 'Proven',
    help: "Inside spend levels we've actually run in these markets.",
  },
  modeled: {
    color: 'warning',
    label: 'Modeled',
    help: 'Past what we have run, projected along the fitted curve. Treat as an estimate.',
  },
  speculative: {
    color: 'default',
    label: 'Directional',
    help: 'Well beyond anything observed — a direction, not a number.',
  },
};

function ZoneChip({ zone }) {
  const meta = ZONE_META[zone];
  if (!meta) return null;
  return (
    <Tooltip title={meta.help}>
      <Chip size="small" variant="outlined" color={meta.color} label={meta.label} />
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// BarsBanner — what this client's markets are measured against.
// Replaces the old goal banner: the goal is now a lens, shown only when someone
// deliberately set one, because the org default was doing the judging for
// almost every client.
// ---------------------------------------------------------------------------
export function BarsBanner({ bars, portfolio }) {
  if (!bars) return null;
  const blend = bars.client_blend ?? portfolio?.blended_com;
  const peer = bars.peer_norm;
  const goal = bars.goal;
  if (blend == null && peer == null && goal == null) return null;

  const beating = blend != null && peer != null && blend <= peer;
  const tone = peer == null ? 'info' : beating ? 'success' : 'warning';
  const bg = {
    success: 'rgba(46, 125, 50, 0.08)',
    warning: 'rgba(237, 108, 2, 0.08)',
    info: 'rgba(2, 136, 209, 0.08)',
  }[tone];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5, mt: 1.5, borderRadius: 2, bgcolor: bg,
        border: '1px solid', borderColor: `${tone}.light`,
      }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        {blend != null && (
          <Chip size="small" color="default" label={`This program: ${pct(blend)}`} />
        )}
        {peer != null && (
          <Chip
            size="small"
            variant="outlined"
            label={`Comparable programs: ${pct(peer)}${
              bars.peer_clients ? ` (${bars.peer_clients})` : ''
            }`}
          />
        )}
        {goal != null && (
          <Chip size="small" variant="outlined" color="primary" label={`Stated goal: ${pct(goal)}`} />
        )}
      </Stack>
      <Typography variant="body2">
        {peer != null && blend != null ? (
          beating ? (
            <>
              This program runs at <b>{pct(blend)}</b> against <b>{pct(peer)}</b> typical for the
              vertical — it is beating comparable programs. Markets below are compared to the rest
              of this program, not to a fixed target.
            </>
          ) : (
            <>
              This program runs at <b>{pct(blend)}</b> against <b>{pct(peer)}</b> typical for the
              vertical. Markets below are compared to the rest of this program; the vertical figure
              is the outside check on whether the program as a whole is healthy.
            </>
          )
        ) : (
          <>
            Markets are compared to the rest of this program rather than to a fixed target — we
            can&apos;t see a client&apos;s other lead sources, so &quot;better than the same dollar
            spent elsewhere here&quot; is the comparison we can stand behind.
          </>
        )}
      </Typography>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// ReallocationPanel — the lead section. Same total, better placed.
// This is the one recommendation that needs nobody's approval and costs the
// client nothing, so it goes first.
// ---------------------------------------------------------------------------
export function ReallocationPanel({ plan }) {
  if (!plan) return null;

  if (!plan.moves?.length) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Best use of the current budget
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {plan.reason || 'No move worth making right now.'}
        </Typography>
      </Paper>
    );
  }

  const before = plan.before || {};
  const after = plan.after || {};
  const apptDelta = (after.appointments || 0) - (before.appointments || 0);
  const revDelta = (after.net_revenue || 0) - (before.net_revenue || 0);

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'success.light' }}>
      <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Best use of the {usd0(plan.total_budget)} already committed
        </Typography>
        <Chip size="small" color="success" label={`${usd0(plan.total_moved)} moved · same total`} />
      </Stack>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {plan.headline}
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Out of</TableCell>
              <TableCell />
              <TableCell>Into</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plan.moves.slice(0, 8).map((m) => (
              <TableRow key={`${m.from_budget_id}-${m.to_budget_id}`}>
                <TableCell sx={{ color: 'text.secondary' }}>{m.from_label}</TableCell>
                <TableCell sx={{ width: 32, p: 0 }}>
                  <ArrowForwardIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{m.to_label}</TableCell>
                <TableCell align="right">{usd0(m.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          Appointments {num(before.appointments)} → <b>{num(after.appointments)}</b>
          {apptDelta > 0 ? ` (+${num(apptDelta)})` : ''}
        </Typography>
        {Boolean(after.net_revenue) && (
          <Typography variant="caption" color="text.secondary">
            Net revenue {usd0(before.net_revenue)} → <b>{usd0(after.net_revenue)}</b>
            {revDelta > 0 ? ` (+${usd0(revDelta)})` : ''}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          Ranked on marginal {plan.basis_label}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        No extra budget required — this is a reallocation inside the total already committed, and
        it is ours to execute.
      </Typography>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// SpendLadder — "you gave us $X; here is what more (or less) would do, and
// where it would go".
// ---------------------------------------------------------------------------
export function SpendLadder({ ladder }) {
  if (!ladder?.by_spend?.length) return null;
  const fmt = costFmt(ladder.unit);
  const cur = ladder.current || {};

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        If the budget changed
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Today: <b>{usd0(cur.spend)}</b>/mo producing about <b>{num(cur.appointments)}</b> appointments
        at <b>{fmt(cur.tolerance)}</b> each. Each row is re-optimised from scratch, so it also shows
        where the money would land.
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Change</TableCell>
              <TableCell align="right">Spend</TableCell>
              <TableCell align="right">Appointments</TableCell>
              <TableCell align="right">{ladder.unit_label}</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell>Where it goes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>Today</TableCell>
              <TableCell align="right">{usd0(cur.spend)}</TableCell>
              <TableCell align="right">{num(cur.appointments)}</TableCell>
              <TableCell align="right">{fmt(cur.tolerance)}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
            {ladder.by_spend.map((row) => {
              const flow = row.multiple > 1 ? row.destinations : row.sources;
              return (
                <TableRow key={row.multiple}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                  <TableCell align="right">{usd0(row.spend)}</TableCell>
                  <TableCell align="right">
                    {num(row.appointments)}
                    {row.delta_appointments ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 0.5, color: row.delta_appointments > 0 ? 'success.main' : 'text.secondary' }}
                      >
                        {row.delta_appointments > 0 ? '+' : ''}
                        {num(row.delta_appointments)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">{fmt(row.blended_cost)}</TableCell>
                  <TableCell><ZoneChip zone={row.zone} /></TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {(flow || []).slice(0, 2).map((d) => d.label).join(', ') || '—'}
                    </Typography>
                    {row.note && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                        {row.note}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CeilingNote ladder={ladder} />
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// CeilingNote — the max-spend answer, and WHICH limit gets there first. Naming
// the binding constraint is the useful part: it says what would raise it.
// ---------------------------------------------------------------------------
export function CeilingNote({ ladder }) {
  const ceiling = ladder?.ceiling;
  if (!ceiling?.value) return null;
  const capacity = ladder.capacity;

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Practical ceiling ≈ {usd0(ceiling.value)}/mo
        </Typography>
        {ceiling.binding && (
          <Chip
            size="small"
            variant="outlined"
            color={ceiling.binding === 'sales_capacity' ? 'secondary' : 'default'}
            label={String(ceiling.binding).replace(/_/g, ' ')}
          />
        )}
      </Stack>
      {ceiling.detail && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {ceiling.detail}
        </Typography>
      )}
      {capacity?.source === 'detected' && capacity.detail && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}>
          {capacity.detail}
        </Typography>
      )}
      {ladder.ramp?.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Getting there stepwise:{' '}
          {ladder.ramp.map((s) => usd0(s.spend)).join(' → ')} — each step re-checked on fresh data
          before the next, so a step that disappoints stops the climb.
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// InMonthPanel — "it's the 27th; how much more can we spend, per division?"
// The question clients actually email mid-month. Same engine as the ladder,
// with time as the binding constraint instead of audience.
// ---------------------------------------------------------------------------
const VERDICT_META = {
  scalable: { color: 'success', label: 'Can take more' },
  expensive: { color: 'warning', label: 'Room, but pricey' },
  at_ceiling: { color: 'default', label: 'At daily ceiling' },
  behind: { color: 'error', label: 'Not deploying' },
  stopped: { color: 'error', label: 'Not spending' },
  too_late: { color: 'default', label: 'Too late' },
};

export function InMonthPanel({ plan }) {
  if (!plan?.segments?.length) return null;
  const fmt = costFmt(plan.basis === 'revenue' ? 'cost_of_marketing' : 'cost_per_appointment');
  const t = plan.totals || {};
  const extra = (t.absorbable_including_expensive || 0) - (t.absorbable || 0);

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Room left this month
        </Typography>
        <Chip
          size="small"
          color={t.absorbable > 0 ? 'success' : 'default'}
          label={`${plan.days_remaining} day${plan.days_remaining === 1 ? '' : 's'} left`}
        />
      </Stack>
      {/* When the committed budget isn't going to be spent, that comes before
          any conversation about adding more. */}
      {plan.underspend_note && (
        <Paper
          elevation={0}
          sx={{
            p: 1.25, mb: 1.5, borderRadius: 1.5,
            bgcolor: 'rgba(237, 108, 2, 0.08)',
            border: '1px solid', borderColor: 'warning.light',
          }}
        >
          <Typography variant="body2">{plan.underspend_note}</Typography>
        </Paper>
      )}
      <Typography variant="body2" sx={{ mb: 1.5 }}>{plan.headline}</Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Division</TableCell>
              <TableCell align="right">Spent so far</TableCell>
              <TableCell align="right">Running at</TableCell>
              <TableCell align="right">Could add</TableCell>
              <TableCell align="right">at ~{plan.basis_label}</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plan.segments.map((s) => {
              const meta = VERDICT_META[s.verdict] || VERDICT_META.at_ceiling;
              return (
                <TableRow key={s.budget_id}>
                  <TableCell sx={{ fontWeight: 600 }}>{s.label}</TableCell>
                  <TableCell align="right">
                    {usd0(s.month_to_date)}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      of {usd0(s.budget)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{usd0(s.daily_rate)}/day</TableCell>
                  <TableCell align="right" sx={{ fontWeight: s.absorbable ? 700 : 400 }}>
                    {s.absorbable ? usd0(s.absorbable) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {s.absorbable ? fmt(s.cost_at_target) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={meta.color} label={meta.label} />
                    {s.reason && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {s.reason}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="body2">
          Deployable before the month closes: <b>{usd0(t.absorbable)}</b>
        </Typography>
        {extra > 0 && (
          <Typography variant="caption" color="text.secondary">
            (+{usd0(extra)} more available in markets where the next dollars come in expensive)
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Based on the most each market has actually sustained per day — a budget can be raised
        faster than delivery can follow it.
      </Typography>
    </Paper>
  );
}
